<div class="stripe-add-card">
  <p class="stripe-add-card-subtitle" *ngIf="!isSaved">
    Enter your card details below. Your information is encrypted and sent directly to Stripe.
  </p>

  <div *ngIf="mountError && !isInitializing" class="stripe-add-card-error-block">
    <i class="pi pi-exclamation-triangle"></i>
    <span>{{ mountError }}</span>
  </div>

  <div class="stripe-add-card-success" *ngIf="isSaved">
    <i class="pi pi-check-circle"></i>
    <span>Card saved.</span>
  </div>

  <div class="stripe-add-card-form" *ngIf="!isSaved && !mountError">
    <div class="stripe-add-card-field" *ngIf="!isInitializing">
      <span class="stripe-add-card-field-label">Full name</span>
      <input
        type="text"
        pInputText
        class="stripe-add-card-text-input"
        placeholder="Name on card"
        [(ngModel)]="fullName"
        [disabled]="isSaving"
        name="stripeAddCardFullName"
      />
    </div>

    <div class="stripe-add-card-field" *ngIf="!isInitializing">
      <span class="stripe-add-card-field-label">Address line 1 <span class="stripe-add-card-optional">(optional)</span></span>
      <input
        type="text"
        pInputText
        class="stripe-add-card-text-input"
        placeholder="Street address"
        [(ngModel)]="addressLine1"
        [disabled]="isSaving"
        name="stripeAddCardAddressLine1"
      />
    </div>

    <div class="stripe-add-card-field">
      <span class="stripe-add-card-field-label" [attr.id]="elementId + '-label'">
        Card details
        <span class="stripe-add-card-brand-badge" *ngIf="cardBrandLabel">{{ cardBrandLabel }}</span>
      </span>

      <p-skeleton *ngIf="isInitializing" height="52px" borderRadius="12px"></p-skeleton>

      <div class="stripe-add-card-element-wrap" *ngIf="!isInitializing">
        <div
          class="stripe-add-card-element-host"
          [class.focused]="isCardFocused"
          [class.invalid]="cardErrorText"
          [class.complete]="isCardComplete"
          [class.busy]="isSaving"
        >
          <div
            class="stripe-add-card-element-mount"
            [id]="elementId"
            [attr.aria-labelledby]="elementId + '-label'"
            role="group"
            #cardElementHost
          ></div>
        </div>

        <!-- Stripe's mounted iframe must stay in the DOM (destroying/remounting it mid-save isn't
             safe), so the "processing" skeleton is a visibility overlay on top of it rather than an
             *ngIf swap — it still reads as "loading" without tearing down the live card field. -->
        <div class="stripe-add-card-element-skeleton-overlay" *ngIf="isSaving">
          <p-skeleton height="52px" borderRadius="12px"></p-skeleton>
        </div>
      </div>
    </div>

    <p class="stripe-add-card-error" *ngIf="cardErrorText">{{ cardErrorText }}</p>

    <div class="stripe-add-card-actions">
      <p-skeleton *ngIf="isInitializing" height="48px" borderRadius="12px" width="100%"></p-skeleton>

      <ng-container *ngIf="!isInitializing">
        <button
          pButton
          type="button"
          class="stripe-add-card-save-btn"
          [loading]="isSaving"
          [label]="isSaving ? 'Saving…' : 'Save card'"
          [disabled]="!canSave"
          (click)="saveCard()"
        ></button>
        <button
          pButton
          type="button"
          class="p-button-text stripe-add-card-cancel-btn"
          label="Cancel"
          *ngIf="cancelled.observers.length"
          [disabled]="isSaving"
          (click)="cancel()"
        ></button>
      </ng-container>
    </div>
  </div>

  <div class="stripe-add-card-trust" *ngIf="!isSaved">
    <i class="pi pi-lock"></i>
    <span>
      Payments are securely processed by
      <svg class="stripe-add-card-trust-logo" viewBox="0 0 468 222.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Stripe">
        <path fill="#635BFF" d="M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.9-2.7 27.7-6.5v-20c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-8.9zm-49.4-9.5c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8zM301.1 67.6c-9.8 0-16.1 4.6-19.6 7.8l-1.3-6.2h-22v116.6l25-5.3.1-28.3c3.6 2.6 8.9 6.3 17.7 6.3 17.9 0 34.2-14.4 34.2-46.1-.1-29-16.6-44.8-34.1-44.8zm-6 68.9c-5.9 0-9.4-2.1-11.8-4.7l-.1-37.1c2.6-2.9 6.2-4.9 11.9-4.9 9.1 0 15.4 10.2 15.4 23.3 0 13.4-6.2 23.4-15.4 23.4zM223.8 61.7l25.1-5.4V36l-25.1 5.3v20.4zM223.8 69.3h25.1v87.5h-25.1zM196.9 76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7 15.9-6.3 19-5.2v-23c-3.2-1.2-14.9-3.4-20.8 7.4zM146.9 47.6l-24.4 5.2-.1 80.1c0 14.8 11.1 25.7 25.9 25.7 8.2 0 14.2-1.5 17.5-3.3v-20.3c-3.2 1.3-19 5.9-19-8.9V90.6h19V69.3h-19l.1-21.7zM79.3 94.7c0-3.9 3.2-5.4 8.5-5.4 7.6 0 17.2 2.3 24.8 6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5 67.6 54 78.2 54 95.9c0 27.6 38.1 23.2 38.1 35.1 0 4.6-4 6.1-9.6 6.1-8.3 0-18.9-3.4-27.3-8v23.8c9.3 4 18.7 5.7 27.3 5.7 20.8 0 35.1-10.3 35.1-28.2-.1-29.8-38.3-24.5-38.3-35.7z"/>
      </svg>
      . LincSell never sees or stores your card number.
    </span>
  </div>
</div>
